import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { SIGNED_IN_HOME } from '../../customerBuild'

/**
 * PublicRoute - Guards public routes and redirects authenticated users
 *
 * Features:
 * - Allows unauthenticated users to access the route
 * - Redirects authenticated users to dashboard (or originally requested page)
 * - Shows loading state while checking auth
 *
 * Use this for routes like landing page, login, signup that should redirect logged-in users.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The public content
 * @param {boolean} props.redirectAuthenticated - If true, redirects authenticated users (default: true)
 */
function PublicRoute({ children, redirectAuthenticated = true }) {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    )
  }

  if (redirectAuthenticated && isAuthenticated) {
    const from = location.state?.from?.pathname
    if (from && from !== '/' && from !== '/login' && from !== '/signup') {
      return <Navigate to={`${from}${location.state.from.search || ''}`} replace />
    }
    return <Navigate to={SIGNED_IN_HOME} replace />
  }

  return children
}

export default PublicRoute
